import { InvoiceGroup } from '@/api/useFreight/types'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { ArrowRightOutlined } from '@ant-design/icons'
import {
  MaterialSymbolIcon,
  Horizontal,
  MaterialIcon,
  addCommasToNumber,
  BBDTag,
  Texto,
  GraviButton,
} from '@gravitate-js/excalibrr'
import { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { TransactionErrorCellRenderer } from '../../FreightLineItems/components/renderers/OrderErrorCellRenderer'
import { InvoiceStatus } from '../Detail/components'
import { invoiceStatusConfig } from '../types'
import { ExtendedColDef, FlatInvoiceItem } from './FlatInvoiceGrid'

export const invoiceFlatColDefs: ExtendedColDef<FlatInvoiceItem>[] = [
  {
    headerName: 'Book Type',
    field: 'book_type',
    flex: 0.25,
    cellStyle: { textTransform: 'uppercase' },
  },
  {
    headerName: 'Group',
    field: 'group_number',
    flex: 1,
    cellStyle: { textTransform: 'uppercase' },
  },
  {
    headerName: 'Invoice Number',
    field: 'invoice_number',
    cellStyle: { textTransform: 'uppercase', fontWeight: 'bold' },
    flex: 1.5,
  },
  {
    headerName: 'Counterparty',
    field: 'counterparty_name',
    flex: 1,
  },
  {
    headerName: 'Start Date',
    field: 'start_date',
    width: 120,
    filter: 'agDateColumnFilter',
    filterParams: {
      browserDatePicker: true,
      comparator: (filterDate: Date, cellValue: Date) => {
        const filterDay = dayjs(filterDate).startOf('day')
        const cellDay = dayjs(cellValue).startOf('day')
        return cellDay.diff(filterDay, 'day')
      },
    },
    valueFormatter: (params) => dayjs(params.value).format('MM/DD/YYYY'),
  },
  {
    headerName: 'End Date',
    field: 'end_date',
    width: 120,
    filter: 'agDateColumnFilter',
    filterParams: {
      browserDatePicker: true,
      comparator: (filterDate: Date, cellValue: Date) => {
        const filterDay = dayjs(filterDate).startOf('day')
        const cellDay = dayjs(cellValue).startOf('day')
        return cellDay.diff(filterDay, 'day')
      },
    },
    valueFormatter: (params) => dayjs(params.value).format('MM/DD/YYYY'),
  },
  {
    headerName: 'Type',
    field: 'type',
    width: 120,
    cellRenderer: (params: ICellRendererParams<string>) => {
      const type = params.value || 'original'
      const iconDict: Record<string, MaterialSymbolIcon> = {
        original: 'document_scanner',
        rebill: 'currency_exchange',
        credit: 'credit_card',
      }
      if (type === 'credit') {
        return (
          <Horizontal style={{ overflow: 'visible', textTransform: 'capitalize' }} verticalCenter gap={4}>
            <MaterialIcon
              style={{ color: 'var(--theme-error)' }}
              size={14}
              icon={iconDict[type as keyof typeof iconDict]}
            />
            {type}
          </Horizontal>
        )
      }
      return (
        <Horizontal style={{ overflow: 'visible', textTransform: 'capitalize' }} verticalCenter gap={4}>
          <MaterialIcon
            style={{
              color: type === 'rebill' ? 'var(--theme-warning)' : 'var(--theme-success)',
            }}
            size={14}
            icon={iconDict[type]}
          />
          {type}
        </Horizontal>
      )
    },
  },
  {
    headerName: 'Order Count',
    field: 'order_count',
    flex: 0.25,
    filter: 'agNumberColumnFilter',
  },
  {
    headerName: 'Total Distance',
    field: 'base_distance',
    type: 'numericColumn',
    flex: 0.25,
    filter: 'agNumberColumnFilter',
    valueFormatter: (params: ValueFormatterParams) => `${addCommasToNumber(params.value)} ${params.data.distance_uom}`,
  },
  {
    headerName: 'Status',
    field: 'status',
    minWidth: 130,
    cellRenderer: (params: ICellRendererParams<string>) => {
      return (
        <Horizontal verticalCenter horizontalCenter style={{ overflow: 'visible' }}>
          <BBDTag
            success={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.success}
            warning={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.warning}
            error={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.error}
            theme2={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.theme2}
            style={{ width: 'fit-content', textTransform: 'capitalize', marginRight: 0 }}
          >
            <Horizontal verticalCenter horizontalCenter gap={8}>
              <MaterialIcon icon={invoiceStatusConfig[params.value as InvoiceStatus]?.icon || 'help'} />
              {invoiceStatusConfig[params.value as InvoiceStatus]?.title}
            </Horizontal>
          </BBDTag>
        </Horizontal>
      )
    },
  },
  {
    headerName: 'Errors',
    field: 'errors',
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => {
      if (params.data.errors.length === 0) return null
      return params.data.errors.map((error) => error.type)
    },
    cellRenderer: TransactionErrorCellRenderer,
  },
  {
    headerName: 'Error Messages',
    field: 'errors.messages',
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => {
      if (params.data.errors.length === 0) return null
      return params.data.errors.map((error) => error.message)
    },
    hide: true,
    suppressColumnsToolPanel: true, // added only for downloading purposes
  },
  {
    headerName: 'Sent Date',
    field: 'sent_date',
    width: 120,
    filter: 'agDateColumnFilter',
    filterParams: {
      browserDatePicker: true,
      comparator: (filterDate: Date, cellValue: Date) => {
        const filterDay = dayjs(filterDate).startOf('day')
        const cellDay = dayjs(cellValue).startOf('day')
        return cellDay.diff(filterDay, 'day')
      },
    },
    valueFormatter: (params) => {
      if (!params.value) return ''
      return dayjs(params.value).format('MM/DD/YYYY')
    },
    cellRenderer: (params: ICellRendererParams) => {
      if (!params.value) {
        return <Texto appearance='hint'>N/A</Texto>
      }
      return dayjs(params.value).format('MM/DD/YYYY')
    },
  },
  {
    headerName: 'Exported',
    field: 'exported_at',
    filter: 'agDateColumnFilter',
    minWidth: 170,
    cellRenderer: (params: ICellRendererParams<string>) => {
      if (params.data?.status !== 'sent' || !params.value) return null
      return (
        <Horizontal style={{ overflow: 'visible' }} verticalCenter horizontalCenter gap={8}>
          <MaterialIcon style={{ color: 'var(--theme-success)' }} icon='check_circle' />
          {formatters.DATE_TIME(params.value)}
        </Horizontal>
      )
    },
  },
  {
    headerName: 'Base',
    field: 'base_amount',
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
  },
  {
    headerName: 'Accessorials',
    field: 'accessorial_amount',
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
  },
  {
    headerName: 'Total',
    field: 'total_amount',
    width: 120,
    pinned: 'right',
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    cellStyle: { fontWeight: 'bold' },
    valueFormatter: (params) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
  },
  {
    headerName: 'Actions',
    field: 'actions' as keyof FlatInvoiceItem,
    width: 60,
    pinned: 'right',
    filter: false,
    excludeFromExport: true,
    cellRenderer: (params: ICellRendererParams) => {
      const navigate = useNavigate()
      const { data, context } = params
      const { groups } = context
      if (!data.order_count || data.order_count === 0) return null

      return (
        <Horizontal style={{ overflow: 'visible' }} gap={2}>
          <GraviButton
            icon={<ArrowRightOutlined />}
            onClick={(e) => {
              // Find the original group data for navigation
              const parentGroup = groups.find((group: InvoiceGroup) => group.id === data.group_id)

              if (!parentGroup) {
                return
              }

              // Get displayed rows from the full groups data
              const displayedRows = groups.filter((g: InvoiceGroup) => g?.id)

              // Find the correct index of this group
              const currentIndex = displayedRows.findIndex((row: InvoiceGroup) => row.id === data.group_id)

              // Use group_id for navigation instead of invoice id
              // This is important because the API expects a group ID, not an invoice ID
              navigate(`/freight/invoices/${data.group_id}`, {
                state: {
                  displayedRows,
                  currentIndex: currentIndex >= 0 ? currentIndex : 0,
                  currentRow: parentGroup, // Use the complete group object
                  filterContext: params.context,
                },
              })
            }}
            size='small'
          />
        </Horizontal>
      )
    },
  },
]
